import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import type { RoleDto } from '@/api-gen/api.schemas';
import type { RoleFormData } from './RoleForm';
import RoleForm from './RoleForm';
import type { UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { getRolesGetRoleCompaniesQueryKey } from '@/api-gen/api';
import RoleSharedCompaniesDisplay from './RoleSharedCompaniesDisplay';
import RoleShareWithCompanyDialog from './RoleShareWithCompanyDialog';

interface RoleDetailDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description: string | null) => Promise<void>;
  role?: RoleDto | null;
  isSaving?: boolean;
  readOnly?: boolean;
  scenarioCompanyId?: number;
  scenarioCompanyName?: string;
  canManageSharing?: boolean;
  onSharingChanged?: () => void;
}

const RoleDetailDialog: React.FC<RoleDetailDialogProps> = ({
  open,
  onClose,
  onSave,
  role,
  isSaving = false,
  readOnly = false,
  canManageSharing = false,
  onSharingChanged
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor, I18nNamespace.Common]);
  const queryClient = useQueryClient();
  const formRef = useRef<UseFormReturn<RoleFormData> | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const isEditMode = !!role;
  const roleOtherOrganizationsCount = Math.max(0, role?.otherCompaniesCount ?? 0);

  const dialogTitle = readOnly
    ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.viewRole))
    : isEditMode
      ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.editRole))
      : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.createRole));

  const handleSave = async (data: RoleFormData) => {
    if (readOnly) return;
    await onSave(data.name, data.description || null);
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit(handleSave)();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh', maxHeight: 800 }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" component="h2">
              {dialogTitle}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {isEditMode && canManageSharing && !readOnly && (
                <IconButton onClick={() => setIsShareDialogOpen(true)} size="small">
                  <ShareIcon />
                </IconButton>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          <Grid container sx={{ height: '100%' }}>
            <Grid item xs={12} md={7}>
              <Box px={3} py={2}>
                <RoleForm role={role} readOnly={readOnly} onFormInit={(form) => (formRef.current = form)} onSubmit={handleSave} formId="role-detail-form" />
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ borderLeft: 1, borderColor: 'divider' }}>
              <Box p={3}>
                <Typography variant="h6" gutterBottom>
                  {translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.sharingSettings)
                  )}
                </Typography>

                {!isEditMode && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {translate(
                      nameof.full<I18nScenarioEditorNs>(
                        (n) => n.scenarioPersonasSettings.roleManagement.saveRoleToManageSharing
                      )
                    )}
                  </Alert>
                )}

                {isEditMode && (
                  <Box sx={{ mt: 2 }}>
                    {!canManageSharing && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {translate(
                          nameof.full<I18nScenarioEditorNs>(
                            (n) => n.scenarioPersonasSettings.roleManagement.sharingReadOnly
                          )
                        )}
                      </Alert>
                    )}
                    <RoleSharedCompaniesDisplay
                      roleId={role?.id}
                      roleName={role?.name}
                      otherOrganizationsCount={roleOtherOrganizationsCount}
                      canManageSharing={canManageSharing}
                      onRefresh={() => {
                        if (role?.id) {
                          queryClient.invalidateQueries(getRolesGetRoleCompaniesQueryKey(role.id));
                        }
                        onSharingChanged?.();
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={onClose} startIcon={readOnly ? <CloseIcon /> : <CancelIcon />}>
            {readOnly
              ? translate('common:' + nameof.full<I18nCommonNs>((n) => n.buttonLabels.close))
              : translate('common:' + nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
          </Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleFormSubmit} startIcon={<SaveIcon />} disabled={isSaving}>
              {isSaving
                ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.saving))
                : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.save))}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <RoleShareWithCompanyDialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        roleId={role?.id}
        roleName={role?.name}
        onShared={() => {
          if (role?.id) {
            queryClient.invalidateQueries(getRolesGetRoleCompaniesQueryKey(role.id));
          }
          onSharingChanged?.();
        }}
      />
    </>
  );
};

export default RoleDetailDialog;
